/**
 * Prüft einen Zustand vom Server, bevor er auf dieses Gerät kommt. Er stammt
 * von anderen Geräten des Bereichs – ist eines davon kompromittiert, soll es
 * hier weder HTML noch kaputte Werte einschleusen können. Ungültige Einträge
 * fallen weg, Texte werden wie eigene Eingaben bereinigt.
 */

import { sanitizeHtml } from '../textProcessor.js';

/** Sitzungs-IDs sind UUIDs oder `sess-…` (sessionManager.generateId) */
const SESSION_ID = /^[\w-]{1,100}$/;

const SETTING_TYPES = {
  apiKey: 'string',
  languageCode: 'string',
  voiceName: 'string',
  speakingRate: 'number',
  pitch: 'number',
  autoplay: 'boolean',
  retentionDays: 'number',
  textSize: 'string',
};

/** Wird als CSS-Klasse gesetzt – nur bekannte Werte */
const TEXT_SIZES = ['small', 'normal', 'large', 'xl'];

const isCount = (n) => Number.isFinite(n) && n >= 0;

/**
 * @param {unknown} state
 * @returns {import('./syncState.js').SyncState}
 */
export function sanitizeRemoteState(state) {
  if (!state || typeof state !== 'object') {
    throw new Error('Die Daten auf dem Server sind beschädigt.');
  }
  const sessions = Array.isArray(state.sessions) ? state.sessions : [];
  return {
    format: state.format,
    sessions: sessions.map(sanitizeSession).filter(Boolean),
    settings: sanitizeSettings(state.settings),
    usage: sanitizeUsage(state.usage),
  };
}

/** @returns {import('../sessionManager.js').Session|null} */
function sanitizeSession(session) {
  const validId = typeof session?.id === 'string' && SESSION_ID.test(session.id);
  if (!validId || !isCount(session.updatedAt)) return null;
  const { id, updatedAt } = session;

  if (session.deletedAt !== undefined) {
    return isCount(session.deletedAt) ? { id, deletedAt: session.deletedAt, updatedAt } : null;
  }
  const { title, rawText, processedHtml, createdAt, lastOpenedAt, totalChars, segmentCount } =
    session;
  const texts = [title, rawText, processedHtml].every((t) => typeof t === 'string');
  const numbers = [createdAt, lastOpenedAt, totalChars, segmentCount].every(isCount);
  if (!texts || !numbers) return null;

  return {
    id,
    title,
    createdAt,
    lastOpenedAt,
    updatedAt,
    totalChars,
    segmentCount,
    rawText,
    processedHtml: sanitizeHtml(processedHtml),
  };
}

function sanitizeSettings(settings) {
  const clean = { values: {}, updatedAt: {} };
  for (const [key, type] of Object.entries(SETTING_TYPES)) {
    const value = settings?.values?.[key];
    const time = settings?.updatedAt?.[key];
    if (typeof value !== type || (type === 'number' && !Number.isFinite(value))) continue;
    if (key === 'textSize' && !TEXT_SIZES.includes(value)) continue;
    clean.values[key] = value;
    if (isCount(time)) clean.updatedAt[key] = time;
  }
  return clean;
}

function sanitizeUsage(usage) {
  const clean = {};
  for (const [device, months] of Object.entries(usage ?? {})) {
    for (const [month, tiers] of Object.entries(months ?? {})) {
      if (!/^\d{4}-\d{2}$/.test(month)) continue;
      for (const [tier, chars] of Object.entries(tiers ?? {})) {
        if (isCount(chars)) ((clean[device] ??= {})[month] ??= {})[tier] = chars;
      }
    }
  }
  return clean;
}
